import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HelpCircle, Lightbulb, ArrowRight } from 'lucide-react';

interface ClarificationMessageProps {
  content: string;
  structured_data: any;
  onSuggestionClick?: (suggestion: string) => void;
}

/**
 * Component for rendering clarification requests with suggestions
 */
export const ClarificationMessage: React.FC<ClarificationMessageProps> = ({ 
  content, 
  structured_data,
  onSuggestionClick 
}) => {
  const needs = structured_data?.needs || [];
  const suggestions = structured_data?.suggestions || [];
  const message = structured_data?.message || content;

  return (
    <Card className="w-full bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <HelpCircle className="h-5 w-5 text-amber-600" />
          Need More Information
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Main message */}
        <div className="p-3 bg-white/60 rounded-lg border border-amber-200/50">
          <p className="text-sm text-gray-700 leading-relaxed">
            {message}
          </p>
        </div>

        {/* What's needed */}
        {needs.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-gray-700">Missing Information:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {needs.map((need: string, index: number) => (
                <Badge 
                  key={index}
                  variant="outline" 
                  className="bg-amber-100 text-amber-800 border-amber-300"
                >
                  {need}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-gray-700">Try These Suggestions:</span>
            </div>
            <div className="space-y-2">
              {suggestions.map((suggestion: string, index: number) => (
                <button
                  key={index}
                  onClick={() => onSuggestionClick?.(suggestion)}
                  className="w-full text-left p-3 bg-white/80 hover:bg-white border border-amber-200/50 hover:border-amber-300 rounded-lg transition-all duration-200 group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">
                      {suggestion}
                    </span>
                    <ArrowRight className="h-4 w-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Help text */}
        <div className="text-xs text-amber-700 bg-amber-100/50 p-2 rounded border border-amber-200/50">
          💡 Click on any suggestion above to use it, or provide more specific details about what you're looking for.
        </div>
      </CardContent>
    </Card>
  );
};